import classnames from 'classnames';
import { UI, Utils as UtilsModule } from 'common';
import Session from 'common/src/code/utils/Session';
import UserRoleEnum from 'common/src/code/utils/UserRole.enum';
import isObject from 'lodash/isObject';
import React from 'react';
import UserActionType from './UserActionType.enum';
import Utils from './Utils';

const {
	WrappedButton,
	WrappedButtonType,
	WrappedInputTextWithLabel,
	WrappedDropdownWithLabel,
	DataDisplay,
	WrappedCheckboxWithLabel,
	WrappedInputNumberWithLabel,
} = UI;
const { UserRole } = UtilsModule;
const userFormInputProperties = (action, isEnterpriseAdmin) => {
	const optionsArray = [
		{
			label: 'First Name:',
			maxLength: 40,
			required: true,
			propertyName: 'firstName',
		},
		{
			label: 'Last Name:',
			maxLength: 40,
			required: true,
			propertyName: 'lastName',
		},
		{
			label: 'Phone:',
			maxLength: 23,
			required: true,
			propertyName: 'phone',
		},
	];

	if (action === UserActionType.Add) {
		if (isEnterpriseAdmin) {
			optionsArray.unshift({
				label: 'Username:',
				maxLength: 14,
				required: true,
				propertyName: 'userName',
			});
		}

		const emailIndex = isEnterpriseAdmin ? 3 : 2;
		optionsArray.splice(emailIndex, 0, {
			label: 'Email:',
			maxLength: 255,
			required: true,
			propertyName: 'email',
		});

		const confirmEmailIndex = isEnterpriseAdmin ? 4 : 3;
		optionsArray.splice(confirmEmailIndex, 0, {
			label: 'Confirm Email:',
			maxLength: 255,
			required: true,
			propertyName: 'confirmEmail',
		});
	}

	return optionsArray;
};

const SingleUserEditView = ({
	selectedUser,
	errorMessage,
	roles,
	handleFormInputChange,
	openResendInviteModal,
	openResetPassWordModal,
	validateFormData,
	action,
	locations,
	disableAllField,
	enterpriseLocation,
	isAccountOwner,
	permissionsReadOnly,
	canManageAccountOwners,
}) => {
	const { status, userName, email, perPrintValue, role, perPrint } =
		selectedUser;

	const userFormCheckboxProperties = [
		{
			propertyName: 'addFunds',
			children: 'Add Funds',
		},
		{
			propertyName: 'viewReports',
			children: 'View Reports',
		},
		{
			propertyName: 'buySupplies',
			children: 'Buy Supplies',
		},
		{
			propertyName: 'costCodesRequired',
			children: 'Cost Codes Required',
		},
		{
			propertyName: 'editCostCodes',
			children: 'Edit Cost Codes',
		},
	];

	const handleInputChange = e => {
		handleFormInputChange(e.target.name, e.target.value);
	};

	const handleCheckbox = e => {
		handleFormInputChange(e.target.name, e.checked);
	};

	const handleInputBlur = e => {
		validateFormData(e.target.name);
	};

	const handleRoleBlur = () => {
		validateFormData('role');
	};

	const getPerPrintLimit = perPrintValue =>
		perPrintValue === Utils.MAXIMUM_LIMIT_AMOUNT ? null : perPrintValue;

	const labelLocationTemplate = option => (
		<div>{`${option.Name} (${option.MeterNo})`}</div>
	);

	const userRole = Session.getUserRole();
	const isEnterpriseAdmin = Utils.checkIsEnterpriseAdmin(userRole);

	// WEBAPPS-32134, Removed hasCanManageRegions() due to CanManageRegions appcap was never actually implemented in v1
	const regionFieldCheck = userRole === UserRoleEnum.CorpAdmin;

	// As a Corp Admin creating a Region Admin will return locations with a Region.
	const buildLocationOptions = () => {
		if (userRole === UserRole.CorpAdmin && role === UserRole.RegionAdmin) {
			// region admin can only manage a location that is part of a region
			// return locations that are assigned a region
			return locations.filter(location => location.ParentCorpId);
		} else {
			return locations;
		}
	};

	const buildRoleOptions = () => {
		// Exclude RegionAdmin role if pre-selected location is not part of a region
		// region admin can only manage a location that is part of a region
		if (
			isObject(enterpriseLocation) &&
			enterpriseLocation.ParentCorpId === null
		) {
			return roles.filter(({ value }) => value !== UserRole.RegionAdmin);
		} else {
			return roles;
		}
	};

	// WEBAPPS-26174, disable location dropdown and pre-select the location when adding a user on Location page
	// check enterpriseLocation existence since it is only passed down from LocationSettingsModal component
	const shouldLocationDisabled =
		isObject(enterpriseLocation) && isObject(enterpriseLocation.MeterAccount);

	return (
		<div className="account-users-single-user-edit">
			{action === UserActionType.UpdateUser && (
				<>
					{isEnterpriseAdmin && (
						<div className="username">
							<DataDisplay
								className="non-editable-field"
								label="Username:"
								data={userName}
							/>
						</div>
					)}
					<WrappedInputTextWithLabel
						label="Email:"
						className="wrapped-field email"
						uiElementConfig={{
							type: 'text',
							name: 'email',
							maxLength: 255,
							required: true,
							dataqa: 'email',
							onChange: handleInputChange,
							value: email,
							className: 'input-email',
							onBlur: handleInputBlur,
							disabled: disableAllField,
						}}
						errorMessage={errorMessage.email ? errorMessage.email[0] : ''}
						isInputInvalid={errorMessage.email}
					/>
					{status === 'Active' && !disableAllField && (
						<div className="reset-password">
							<WrappedButton
								buttonType={WrappedButtonType.TertiaryGrey}
								label="Reset Password"
								dataqa="reset-password"
								onClick={() => openResetPassWordModal()}
							/>
						</div>
					)}
					<div className="status">
						<DataDisplay
							className="non-editable-field"
							label="Status:"
							data={status}
						/>
					</div>
					{status === 'Invited' && (
						<div className="resend-invite">
							<WrappedButton
								buttonType={WrappedButtonType.TertiaryGrey}
								label="Resend Invite"
								dataqa="resend-invite"
								onClick={openResendInviteModal}
								disabled={disableAllField}
							/>
						</div>
					)}
					{isEnterpriseAdmin && action !== UserActionType.Add && (
						<div className="location">
							<DataDisplay
								className="non-editable-field"
								label="Location:"
								data={selectedUser.location}
							/>
						</div>
					)}
					{regionFieldCheck && (
						<div className="region">
							<DataDisplay
								className="non-editable-field"
								label="Region:"
								data={selectedUser.region}
							/>
						</div>
					)}
				</>
			)}
			{userFormInputProperties(action, isEnterpriseAdmin).map(
				({ propertyName, label, maxLength, required }) => (
					<WrappedInputTextWithLabel
						key={`input-${propertyName}`}
						label={label}
						className={classnames('wrapped-field', propertyName)}
						uiElementConfig={{
							type: 'text',
							name: propertyName,
							maxLength,
							required,
							dataqa: propertyName,
							onChange: handleInputChange,
							value: selectedUser[propertyName],
							className: `input-${propertyName}`,
							onBlur: handleInputBlur,
							disabled: disableAllField,
						}}
						errorMessage={
							errorMessage[propertyName] ? errorMessage[propertyName][0] : ''
						}
						isInputInvalid={errorMessage[propertyName]}
					/>
				)
			)}
			{isAccountOwner && !canManageAccountOwners ? (
				<DataDisplay
					className="wrapped-field role non-editable-field"
					label="Role:"
					data="Account Owner"
				/>
			) : (
				<WrappedDropdownWithLabel
					label="Role:"
					className="wrapped-field role"
					uiElementConfig={{
						name: 'role',
						placeholder: 'Select Role',
						options: buildRoleOptions(),
						optionLabel: 'label',
						optionValue: 'value',
						dataqa: 'role-selection',
						value: role,
						onChange: handleInputChange,
						onBlur: handleRoleBlur,
						disabled: disableAllField || permissionsReadOnly,
					}}
					errorMessage={errorMessage.role ? errorMessage.role[0] : ''}
					isInputInvalid={errorMessage.role}
				/>
			)}
			{Utils.checkIsEnterpriseAdmin(userRole) &&
				action === UserActionType.Add && (
					<WrappedDropdownWithLabel
						label="Location:"
						className={classnames(
							'wrapped-field location',
							errorMessage.role ? 'error-field' : ''
						)}
						uiElementConfig={{
							name: 'location',
							placeholder: 'Select Location',
							options: buildLocationOptions(),
							value: selectedUser.location,
							onChange: handleInputChange,
							optionValue: 'MeterNo',
							optionLabel: 'Name',
							dataqa: 'location-selection',
							itemTemplate: labelLocationTemplate,
							dataKey: 'MeterNo',
							disabled: shouldLocationDisabled || disableAllField,
						}}
						errorMessage={errorMessage.location ? errorMessage.location[0] : ''}
						isInputInvalid={errorMessage.location}
					/>
				)}

			<div className="user-caps-checkbox">
				{userFormCheckboxProperties.map(({ propertyName, children }, i) => (
					<WrappedCheckboxWithLabel
						key={propertyName}
						label={i === 0 ? 'Permissions:' : ' '}
						uiElementConfig={{
							inputId: propertyName,
							dataqa: propertyName,
							name: propertyName,
							children,
							onChange: handleCheckbox,
							checked: selectedUser[propertyName],
							disabled: disableAllField || permissionsReadOnly,
						}}
					/>
				))}
			</div>
			<div className="per-print-limit">
				<WrappedCheckboxWithLabel
					label=" "
					uiElementConfig={{
						dataqa: 'per-print-limit-checkbox',
						name: 'perPrint',
						children: <span>Per-print maximum of : &nbsp;</span>,
						onChange: handleCheckbox,
						checked: perPrint,
						disabled: disableAllField || permissionsReadOnly,
					}}
				/>
				<WrappedInputNumberWithLabel
					className="input-per-print-value"
					uiElementConfig={{
						dataqa: 'per-print-limit-input',
						name: 'perPrintValue',
						disabled: !perPrint || disableAllField || permissionsReadOnly,
						value: getPerPrintLimit(perPrintValue),
						onChange: handleInputChange,
						step: 1,
						min: 0,
						max: Utils.MAXIMUM_LIMIT_AMOUNT,
						minFractionDigits: 2,
						maxFracionDigits: 2,
						mode: 'currency',
						currency: 'USD',
						locale: 'en-US',
						showButtons: true,
						onBlur: handleInputBlur,
					}}
					errorMessage={
						errorMessage.perPrintValue ? errorMessage.perPrintValue[0] : ''
					}
					isInputInvalid={errorMessage.perPrintValue}
				/>
			</div>
		</div>
	);
};
export default SingleUserEditView;
